import { Icon, Tooltip } from "@components/library";
import RelevanceFeedbackModal from "@components/modals/RelevanceFeedbackModal";
import { COLORS, FONTS, STYLES } from "@constants";
import useLocalStorage from "@hooks/useLocalStorage";
import { toggleLoginModal } from "@redux/actions/profileActions";
import { RootState } from "@redux/store";
import {
  getUserRequestPreference,
  updateUserRequestPreference,
} from "@requests/user_request_preferences";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { UserRequestPreferenceSource } from "@tsTypes/__generated__/enums/user_request_preference_source";
import { LoginModalPreviousAction } from "@tsTypes/redux";
import { RequestUnion } from "@tsTypes/requests";
import appsignal from "@utils/appsignal";
import { track } from "@utils/appUtils";
import { setPrevUrlCookie } from "@utils/authUtils";
import { t } from "@utils/i18n";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { TOOLTIP_CONTENT } from "./tooltipContent";

interface RefineRecommendationsProps {
  request: RequestUnion;
  isComplete: boolean;
  trackingProps?: Record<string, any>;
}

function useRequestPreferences(requestId: number, isLoggedIn: boolean) {
  const [localPreference, setLocalPreference] = useLocalStorage<boolean | null>(
    `request_preference_${requestId}`,
    null
  );

  const queryClient = useQueryClient();
  const queryKey = ["userRequestPreference", requestId, isLoggedIn, localPreference];

  const { data: isInterestedQuery } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      if (isLoggedIn) {
        const preference = await getUserRequestPreference(requestId);
        return preference?.is_interested ?? null;
      }
      return localPreference;
    },
  });

  const { mutate: updateInterest } = useMutation({
    mutationFn: async ({ isInterested, reason }: { isInterested: boolean; reason?: string }) => {
      if (isLoggedIn) {
        return updateUserRequestPreference({
          requestId,
          isInterested,
          source: UserRequestPreferenceSource.REQUEST_PAGE,
          reason,
        });
      }
      setLocalPreference(isInterested);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
    onError: (error: Error) => {
      appsignal.sendError(error, (span) => {
        span.setAction("RefineRecommendations#updatePreference");
        span.setTags({
          requestId: String(requestId),
        });
      });
    },
  });

  return {
    isInterested: isInterestedQuery,
    updateInterest,
  };
}

const i18nPrefix = "components.refine_recommendations";

const RefineRecommendations: React.FC<RefineRecommendationsProps> = ({
  request,
  trackingProps = {},
  isComplete,
}) => {
  const [isRelevant, setIsRelevant] = useState<boolean | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);

  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const isLoggedIn = currentUser.isLoggedIn;

  const { isInterested, updateInterest } = useRequestPreferences(request.id, isLoggedIn);

  const handleRelevantClick = () => {
    if (isInterested) return;

    track(SegmentEventName.CLICK, {
      react_component: "RefineRecommendations",
      ui_component: "Relevant button",
      request_slug: request.slug,
      is_logged_in: isLoggedIn,
      ...trackingProps,
    });

    if (!isComplete) {
      setIsRelevant(true);
      setShowModal(true);
    } else {
      updateInterest({ isInterested: true });
    }
  };

  const handleNotRelevantClick = () => {
    if (isInterested === false) return;

    track(SegmentEventName.CLICK, {
      react_component: "RefineRecommendations",
      ui_component: "Not relevant button",
      request_slug: request.slug,
      is_logged_in: isLoggedIn,
      ...trackingProps,
    });

    setIsRelevant(false);
    setShowModal(true);
  };

  const handleFeedbackSubmit = (reason?: string, skipLoginModal?: boolean) => {
    if (isRelevant === undefined) return;

    track(SegmentEventName.SUBMIT, {
      react_component: "RefineRecommendations",
      ui_component: isRelevant ? "Relevant feedback" : "Not relevant feedback",
      request_slug: request.slug,
      selected_option: reason,
      is_logged_in: isLoggedIn,
      ...trackingProps,
    });

    updateInterest({ isInterested: isRelevant, reason });
    setShowModal(false);

    if (!isLoggedIn && !skipLoginModal) {
      setPrevUrlCookie(window.location.pathname);
      dispatch(
        toggleLoginModal({
          open: true,
          previousAction: LoginModalPreviousAction.REFINE_RECOMMENDATIONS,
        })
      );
    }
  };

  const handleModalClose = () => {
    handleFeedbackSubmit(undefined, true);
  };

  return (
    <>
      <Container>
        <SectionHeading>
          <Icon name="Magic" color={COLORS.NEUTRAL_700} size="md" />
          <HeadingText>{t([i18nPrefix, "header"])}</HeadingText>
        </SectionHeading>
        <QuestionText>{t([i18nPrefix, "question"])}</QuestionText>
        <ButtonContainer>
          <RelevanceButton
            onClick={handleRelevantClick}
            $selected={isInterested}
            aria-label={t([i18nPrefix, "buttons.relevant.aria_label"])}
          >
            <Tooltip
              position="left"
              tooltipWidth="275px"
              contentWidth="100%"
              arrowOffsetTop={"36px"}
              remainOnHover={false}
              asPortal
              content={TOOLTIP_CONTENT.RELEVANT.NOT_SIGNED_IN}
              // Uncomment this when Saved Requests page is built:
              // content={TOOLTIP_CONTENT.RELEVANT[isLoggedIn ? "SIGNED_IN" : "NOT_SIGNED_IN"]}
              // arrowOffsetTop={isLoggedIn ? "56px" : "36px"}
            >
              <ButtonContent>
                <Icon name="Checkmark" color={COLORS.BLUE_500} size="md" />
                {t([i18nPrefix, "buttons.relevant.text"])}
              </ButtonContent>
            </Tooltip>
          </RelevanceButton>
          <RelevanceButton
            onClick={handleNotRelevantClick}
            $selected={isInterested === false}
            aria-label={t([i18nPrefix, "buttons.not_relevant.aria_label"])}
          >
            <Tooltip
              position="left"
              tooltipWidth="275px"
              contentWidth="100%"
              arrowOffsetTop="45px"
              remainOnHover={false}
              asPortal
              content={TOOLTIP_CONTENT.NOT_RELEVANT}
            >
              <ButtonContent>
                <Icon name="Block" color={COLORS.NEUTRAL_500} size="md" />
                {t([i18nPrefix, "buttons.not_relevant.text"])}
              </ButtonContent>
            </Tooltip>
          </RelevanceButton>
        </ButtonContainer>
      </Container>
      <RelevanceFeedbackModal
        request={request}
        isOpen={showModal}
        isRelevant={isRelevant}
        trackingProps={trackingProps}
        onClose={handleModalClose}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );
};

export default RefineRecommendations;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  width: 100%;
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const HeadingText = styled.div`
  ${FONTS.SEMIBOLD_1};
  color: ${COLORS.NEUTRAL_800};
`;

const QuestionText = styled.div`
  ${FONTS.REGULAR_2};
  color: ${COLORS.NEUTRAL_500};
  margin-bottom: 16px;
`;
const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;
const RelevanceButton = styled.button`
  display: flex;
  flex: 1;
  height: 43px;
  padding: 0;
  color: ${COLORS.BLUE_500};
  ${FONTS.BUTTON_3}
  box-shadow: ${STYLES.SHADOW_CARD};
  border: none;
  outline: 1px solid ${({ $selected }) => ($selected ? COLORS.HALO_BLUE : COLORS.NEUTRAL_250)};
  background: ${({ $selected }) => ($selected ? COLORS.BLUE_LIGHT_300 : COLORS.WHITE)};
  ${({ $selected }) => $selected && `z-index: 1;`}

  :first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }
  :last-child {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    color: ${COLORS.NEUTRAL_500};
  }

  &:hover {
    outline-color: ${COLORS.BLUE_450};
    background-color: ${({ $selected }) => ($selected ? COLORS.BLUE_LIGHT_50 : COLORS.NEUTRAL_50)};
    z-index: 1;
  }
`;
const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 43px;
`;
